
import React, { useState } from 'react';
import { ChevronDownIcon } from '../components/Icons';
import { Page } from '../types';
import PageWrapper from '../components/PageWrapper';

const faqs = [
  {
    question: "Do I need to know how to code?",
    answer: <p>Not at all! Codemine is designed for everyone. You can describe your idea in plain English, and our AI assistant, Codey, will write the code for you. If you do know how to code, you'll appreciate the high-quality, readable code that you can easily build upon. It's the perfect launchpad for both beginners and experts.</p>
  },
  {
    question: "How do I use Codemine and Codey effectively?",
    answer: (
      <div className="space-y-4">
        <p>Using Codemine is an interactive process designed to give you both speed and control.</p>
        <p><strong>Step 1: Describe your idea</strong></p>
        <p>Start by describing the application you want to build. Be descriptive! For example, "A modern, dark-themed portfolio website for a photographer named Jane Doe, with a gallery and a contact form." Codey will analyze this and generate the application for you.</p>
        <p><strong>Step 2: Refine Your Design with Codey Edit</strong></p>
        <p>Once your app is built, you can make changes using the chat prompt. This is best for things like: "Change the entire color scheme to a light, minimalist theme," or "Add a new section for customer testimonials above the footer." For minor text changes, use the "Quick Edit" toggle in the preview pane to click and edit directly.</p>
      </div>
    )
  },
    {
    question: "What are tokens and how does token tracking work?",
    answer: (
        <div className="space-y-4">
            <p>Codemine uses a token-based system that tracks your <strong>actual AI usage</strong>. Tokens are the units that AI models use to process text - both your input prompts and the generated code output.</p>
            <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Accurate Tracking:</strong> If 1,200 tokens are used to generate your project, exactly 1,200 tokens are deducted from your plan. We track both input tokens (your prompt) and output tokens (generated code).</li>
                <li><strong>Token Limits by Plan:</strong>
                  <ul className="list-none pl-4 mt-2 space-y-1">
                    <li>• <strong>Starter:</strong> 200 tokens/day, 3,000 tokens/month</li>
                    <li>• <strong>Creator:</strong> 400 tokens/day, 12,000 tokens/month</li>
                    <li>• <strong>Pro:</strong> 600 tokens/day, 30,000 tokens/month</li>
                    <li>• <strong>Business:</strong> 850 tokens/day, 100,000 tokens/month</li>
                  </ul>
                </li>
                <li><strong>Reset Schedule:</strong> Daily limits reset every 24 hours. Monthly limits reset every 30 days.</li>
                <li><strong>Real-Time Deduction:</strong> Tokens are deducted immediately after each generation completes, based on actual consumption.</li>
                <li><strong>No Rollover:</strong> Unused tokens do not roll over to the next day or month. Each reset starts fresh with your plan's limits.</li>
            </ul>
            <p className="mt-2">This ensures fair, transparent usage tracking - you get consistent daily and monthly limits based on your plan tier.</p>
        </div>
    )
  },
  {
    question: "What types of projects can I build?",
    answer: (
        <div className="space-y-4">
            <p>Codemine can generate a wide variety of web-based projects:</p>
            <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Websites:</strong> Landing pages, portfolios, blogs, business sites</li>
                <li><strong>Web Apps:</strong> Todo lists, dashboards, e-commerce sites, SaaS platforms</li>
                <li><strong>Mobile Apps:</strong> React Native apps that work on iOS and Android</li>
                <li><strong>Games:</strong> Browser-based games with canvas and interactive elements</li>
            </ul>
            <p className="mt-2">All projects are generated with high-quality HTML, CSS, and JavaScript code that you can customize and deploy anywhere.</p>
        </div>
    )
  },
  {
    question: "Who owns the code that Codemine generates?",
    answer: <p>You do. Subject to your compliance with our Terms of Service, you own the full rights to the HTML, CSS, and JavaScript code generated for your projects. You are free to use it for personal or commercial purposes, modify it, and host it anywhere you like. Our goal is to empower you, not to lock you into our platform.</p>
  },
  {
    question: "Where are my projects and files stored?",
    answer: (
        <div className="space-y-4">
            <p>Your project data is stored securely in the cloud with enterprise-grade security:</p>
            <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Project Files:</strong> All generated code and assets are stored in secure cloud object storage with access controls and encryption.</li>
                <li><strong>Project Metadata:</strong> Your project names, types, and settings are stored in secure databases with row-level security policies.</li>
                <li><strong>User Profiles:</strong> Your account information, subscription plan, and token usage are stored securely with encryption.</li>
                <li><strong>Authentication:</strong> We use secure OAuth authentication - we never store passwords ourselves.</li>
            </ul>
            <p className="mt-2">All data transmission is encrypted with HTTPS/TLS. You can export your project files at any time from the dashboard.</p>
        </div>
    )
  },
  {
    question: "What is PWA and can I install Codemine on my phone?",
    answer: (
        <div className="space-y-4">
            <p>Yes! Codemine is a <strong>Progressive Web App (PWA)</strong>, which means you can install it on your phone, tablet, or desktop just like a native app.</p>
            <p><strong>How to Install:</strong></p>
            <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>iOS (Safari):</strong> Tap the Share button → "Add to Home Screen"</li>
                <li><strong>Android (Chrome):</strong> Tap the menu (⋮) → "Install app" or "Add to Home Screen"</li>
                <li><strong>Desktop (Chrome/Edge):</strong> Click the install icon in the address bar</li>
            </ul>
            <p><strong>Benefits:</strong> Offline access to cached pages, app-like experience, faster loading, and easy access from your home screen. Note that code generation requires an internet connection.</p>
        </div>
    )
  },
  {
    question: "Can I turn my web project into a mobile app?",
    answer: (
        <div className="space-y-4">
            <p>Yes! Codemine offers mobile deployment services that convert your web projects into native iOS and Android apps. We have two options:</p>
            <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Codemine Deploy (PKR7,999/month):</strong> We handle everything - no Apple/Google developer accounts needed. Fast and simple.</li>
                <li><strong>Your Account Deploy (PKR4,999/month):</strong> Deploy under your own developer accounts for full ownership and control.</li>
            </ul>
            <p className="mt-2">The deployment process typically takes 5-10 business days including app store review. Visit our Mobile Deployment page for more details!</p>
        </div>
    )
  },
  {
    question: "What happens if I cancel my subscription?",
    answer: (
      <div className="space-y-4">
        <p>When you cancel your subscription, here's what happens at the end of your current billing cycle:</p>
        <ul className="list-disc list-inside space-y-2 pl-4">
          <li><strong>Account Downgrade:</strong> Your account will be downgraded to the free "Starter" plan with 50 daily tokens.</li>
          <li><strong>Unused Tokens:</strong> Any unused tokens from your paid plan will be forfeited. Tokens do not roll over or get refunded.</li>
          <li><strong>Token Resets:</strong> You'll start fresh with 50 tokens per day on the Starter plan (resets every 24 hours).</li>
          <li><strong>Live Projects:</strong> Your deployed projects will be taken offline. Hosting is a feature of paid plans only. Your project files remain safe in secure storage.</li>
          <li><strong>Project Access:</strong> All your projects and code remain in your dashboard. You can view, download, and export them at any time.</li>
          <li><strong>Mobile Deployment:</strong> If you have mobile deployment subscriptions, those will also be canceled and your apps may be removed from app stores if deployed under Codemine accounts.</li>
        </ul>
        <p>You can resubscribe at any time to restore full access and redeploy your projects.</p>
      </div>
    )
  },
  {
    question: "What happens to my projects if I delete my account?",
    answer: (
        <div className="space-y-4">
            <p>If you delete your Codemine account, here's what happens to your data:</p>
            <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong>Personal Information:</strong> Your account data, email, and profile are permanently deleted within 90 days.</li>
                <li><strong>Project Files:</strong> All your generated code and project files are permanently deleted within 90 days.</li>
                <li><strong>Project Metadata:</strong> Your project names, types, and settings are removed from our database within 90 days.</li>
                <li><strong>Token Usage:</strong> Historical usage data is retained for billing and regulatory compliance purposes.</li>
                <li><strong>Backup Data:</strong> We maintain backups for disaster recovery, retained for up to 90 days, then permanently deleted.</li>
            </ul>
            <p className="mt-2"><strong>Important:</strong> Account deletion is permanent and cannot be undone. Make sure to download your projects before deleting your account!</p>
        </div>
    )
  },
  {
    question: "How does Codemine handle my data and privacy?",
    answer: <p>We take your privacy very seriously. We only collect the data necessary to provide and improve our service. Your prompts and generated code are processed by our AI systems. We track actual token usage (input and output tokens) for billing purposes. All data is encrypted in transit (HTTPS/TLS) and at rest. We do not sell your personal data. For a complete overview, please read our full <button onClick={() => (window as any).navigateToPrivacy()} className="text-[var(--gradient-to)] underline">Privacy Policy</button>.</p>
  },
  {
    question: "Can I use Codemine offline?",
    answer: (
        <div className="space-y-4">
            <p>Partially. As a Progressive Web App (PWA), Codemine can cache static assets (HTML, CSS, JavaScript) locally on your device for offline access.</p>
            <p><strong>What works offline:</strong></p>
            <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Viewing the Codemine homepage and navigation</li>
                <li>Accessing cached pages</li>
                <li>Reading documentation (if previously loaded)</li>
            </ul>
            <p className="mt-2"><strong>What requires internet:</strong></p>
            <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Code generation (requires AI connection)</li>
                <li>Loading projects from cloud storage</li>
                <li>Authentication and account access</li>
                <li>Deploying projects</li>
                <li>Mobile app submissions</li>
            </ul>
            <p className="mt-2">Think of offline mode as a way to browse Codemine when you're offline, but you'll need an internet connection to create or modify projects.</p>
        </div>
    )
  },
];


const FAQItem: React.FC<{ faq: { question: string; answer: React.ReactNode }, id?: string }> = ({ faq, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-[var(--border-color)] py-6" id={id}>
      <button
        className="w-full flex justify-between items-center text-left"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg font-semibold text-[var(--text-primary)]">{faq.question}</h3>
        <ChevronDownIcon className={isOpen ? 'rotate-180' : ''} />
      </button>
      <div
        className={`grid grid-rows-[0fr] transition-all duration-500 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] mt-4' : ''
        }`}
      >
        <div className="overflow-hidden">
          <div className="prose prose-invert max-w-none prose-p:text-[var(--text-secondary)] prose-strong:text-[var(--text-primary)] prose-ul:text-[var(--text-secondary)] prose-li:marker:text-[var(--text-secondary)] max-h-[30rem] overflow-y-auto pr-4">
              {faq.answer}
          </div>
        </div>
      </div>
    </div>
  );
};

interface FAQPageProps {
    navigateTo: (page: Page) => void;
}

const FAQPage: React.FC<FAQPageProps> = ({ navigateTo }) => {
    // Expose navigateTo to the window for use inside the FAQ answer elements
    (window as any).navigateToPrivacy = () => navigateTo('privacy');

    return (
        <PageWrapper>
            <div className="py-20 sm:py-32">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-[var(--panel-bg)] backdrop-blur-sm border border-[var(--border-color)] rounded-2xl p-8 md:p-12 lg:p-16 max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <h1 className="text-4xl md:text-5xl font-extrabold text-[var(--text-primary)]">
                                Frequently Asked Questions
                            </h1>
                            <p className="mt-4 text-lg text-[var(--text-secondary)]">
                                Find answers to common questions about Codemine.
                            </p>
                        </div>
                        
                        <div>
                            {faqs.map((faq, index) => (
                                <FAQItem key={index} faq={faq} id={(faq as any).id} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </PageWrapper>
    );
};

export default FAQPage;